import React, { useEffect, useMemo, useState } from "react";
import { TextField, Button } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import "./auth.css";
import usePageTitle from "../hooks/usePageTitle";

const ResetPassword = () => {
  usePageTitle("Reset Password");
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return String(params.get("token") || "").trim();
  }, [location.search]);
  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState({
    letters: false,
    number: false,
    special: false,
    match: false,
  });

  useEffect(() => {
    const { password, confirmPassword } = formData;
    const hasFiveLetters = (password.match(/[A-Za-z]/g) || []).length >= 5;
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const match = password === confirmPassword && password.length > 0;

    setValidation({
      letters: hasFiveLetters,
      number: hasNumber,
      special: hasSpecial,
      match,
    });
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!token) {
      setMessage("Reset link is invalid or missing.");
      return;
    }
    if (!Object.values(validation).every(Boolean)) {
      setMessage("❌ Please meet all password requirements.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/api/reset-password", {
        token,
        password: formData.password,
      });
      setMessage(data?.message || "Password updated successfully.");
      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      setMessage(error?.response?.data?.message || "Error updating password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-sidebar">
          <h3>Reset Your Password</h3>
          <p>Enter a new password below.</p>
        </div>

        <div className="auth-form">
          <h2>Reset Password</h2>
          <form onSubmit={handleSubmit}>
            <TextField
              label="New Password"
              fullWidth
              size="small"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              required
              sx={{ mb: 0.75 }}
            />
            <TextField
              label="Confirm Password"
              fullWidth
              size="small"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              required
              sx={{ mb: 0.75 }}
            />
            <ul style={{ listStyle: "none", padding: 0, fontSize: "0.84rem", marginTop: "4px", marginBottom: "6px", lineHeight: 1.25 }}>
              <li style={{ color: validation.letters ? "green" : "red" }}>
                {validation.letters ? "✅" : "❌"} At least 5 letters
              </li>
              <li style={{ color: validation.number ? "green" : "red" }}>
                {validation.number ? "✅" : "❌"} Includes a number
              </li>
              <li style={{ color: validation.special ? "green" : "red" }}>
                {validation.special ? "✅" : "❌"} Includes a special character
              </li>
              <li style={{ color: validation.match ? "green" : "red" }}>
                {validation.match ? "✅" : "❌"} Passwords match
              </li>
            </ul>
            <Button type="submit" variant="contained" fullWidth className="auth-button" disabled={submitting} sx={{ mt: 0.5 }}>
              {submitting ? "Updating..." : "Update Password"}
            </Button>
          </form>
          <p className="message">{message}</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
